import React, { useEffect, useRef, useState } from 'react';
import { ICampaignDeliverables, ITemplate } from '../../../interfaces/campaign.interface';
import {
    AssetType,
    DELIVERABLE_PLATFORM_ARRAY,
    DeliverableTask,
    Platform,
    getAssetTypeForPlatform,
} from '../../../constants/campaign';
import AttachTemplateOrTags from './AttachTemplateOrTags';
import { Dropdown, Form, InputGroup } from 'react-bootstrap';
import AttachProductImage from './AttachProductImage';
import { getDeliverableNumber } from '../../../utils/campaignUtils';

interface IProps {
    index: number;
    deliverable: ICampaignDeliverables;
    deliverables: ICampaignDeliverables[];
    updateDeliverable: (index: number, deliverable: ICampaignDeliverables) => void;
    addSubDeliverable: (deliverableNumber: number, isResize: boolean) => void;
    deleteDeliverables: (deliverableNumber: number) => void;
}

const Deliverable = ({
    index,
    deliverable,
    deliverables,
    updateDeliverable,
    addSubDeliverable,
    deleteDeliverables,
}: IProps) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [isEditing, setIsEditing] = useState(false);

    const updateDeliverableTags = (tags: string[]) => {
        const updatedDeliverable: ICampaignDeliverables = { ...deliverable };
        updatedDeliverable.tags = tags;
        updateDeliverable(index, updatedDeliverable);
    };

    const updateDeliverableUserProductPhoto = (productImageUrl: string) => {
        const updatedDeliverable: ICampaignDeliverables = { ...deliverable };
        updatedDeliverable.user_product_path = productImageUrl;
        if (!productImageUrl || productImageUrl.length === 0) {
            updatedDeliverable.ad_product_photo = false;
        }
        updateDeliverable(index, updatedDeliverable);
    };

    const updateDeliverableAdProductPhoto = (adProductPhoto: boolean) => {
        const updatedDeliverable: ICampaignDeliverables = { ...deliverable };
        updatedDeliverable.ad_product_photo = adProductPhoto;
        updateDeliverable(index, updatedDeliverable);
    };

    const updateDeliverableTemplate = (templateDetail: ITemplate | null) => {
        const updatedDeliverable: ICampaignDeliverables = { ...deliverable };
        updatedDeliverable.templateDetails = templateDetail;
        updateDeliverable(index, updatedDeliverable);
    };

    const updateDeliverableTagsAndTemplate = (tags: string[], templateDetail: ITemplate | null) => {
        const updatedDeliverable: ICampaignDeliverables = { ...deliverable };
        updatedDeliverable.tags = tags;
        updatedDeliverable.templateDetails = templateDetail;
        updateDeliverable(index, updatedDeliverable);
    };

    const updateDeliverableTask = (task: DeliverableTask) => {
        if (deliverable.task === task) return;
        const updatedDeliverable: ICampaignDeliverables = { ...deliverable };
        updatedDeliverable.task = task;
        updatedDeliverable.content = '';
        if (task === DeliverableTask.PRODUCT_PHOTOGRAPHY) {
            updatedDeliverable.platform = undefined;
            updatedDeliverable.asset_type = undefined;
        } else if (task === DeliverableTask.CREATE_NEW_AD) {
            updatedDeliverable.platform = Platform.INSTAGRAM;
            updatedDeliverable.asset_type = getAssetTypeForPlatform(Platform.INSTAGRAM)[0];
        }
        updateDeliverable(index, updatedDeliverable);
    };

    const updateDeliverablePlatform = (platform: Platform) => {
        if (deliverable.platform === platform) return;
        const updatedDeliverable: ICampaignDeliverables = { ...deliverable };
        updatedDeliverable.platform = platform;
        if (updatedDeliverable.task === DeliverableTask.CREATE_NEW_AD) {
            updatedDeliverable.asset_type = getAssetTypeForPlatform(platform)[0];
        }
        if (
            updatedDeliverable.templateDetails &&
            (updatedDeliverable.templateDetails.platform !== updatedDeliverable.platform ||
                updatedDeliverable.templateDetails.asset_type !== updatedDeliverable.asset_type)
        ) {
            updatedDeliverable.templateDetails = null;
        }
        updateDeliverable(index, updatedDeliverable);
    };

    const updateDeliverableAssetType = (assetType: AssetType) => {
        if (deliverable.asset_type === assetType) return;
        const updatedDeliverable: ICampaignDeliverables = { ...deliverable };
        updatedDeliverable.asset_type = assetType;
        if (
            updatedDeliverable.templateDetails &&
            (updatedDeliverable.templateDetails.platform !== updatedDeliverable.platform ||
                updatedDeliverable.templateDetails.asset_type !== updatedDeliverable.asset_type)
        ) {
            updatedDeliverable.templateDetails = null;
        }
        updateDeliverable(index, updatedDeliverable);
    };

    const updateDeliverableContent = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const updatedDeliverable: ICampaignDeliverables = { ...deliverable };
        updatedDeliverable.content = event.target.value;
        updateDeliverable(index, updatedDeliverable);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            setIsEditing(false);
        }
    };

    useEffect(() => {
        if (isEditing && textareaRef.current) {
            const el = textareaRef.current;
            el.focus();
            el.setSelectionRange(el.value.length, el.value.length);
        }
    }, [isEditing]);

    return (
        <div className="tw-w-full tw-flex tw-items-start tw-gap-x-2">
            <span className="tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-full tw-font-bold tw-bg-purple-500 tw-text-white tw-text-base tw-p-3">
                {getDeliverableNumber(deliverables, deliverable.deliverable_number)}
            </span>
            <div className="tw-grow tw-flex tw-items-stretch tw-border tw-rounded-md tw-border-gray-250">
                <div className="tw-min-h-[60px] tw-max-h-[60px] md:tw-min-h-[87px] md:tw-max-h-[128px] tw-bg-gray-250 tw-w-[60px] md:tw-w-[128px]">
                    <AttachTemplateOrTags
                        deliverable={deliverable}
                        updateDeliverableTags={updateDeliverableTags}
                        updateDeliverableTemplate={updateDeliverableTemplate}
                        updateDeliverableTagsAndTemplate={updateDeliverableTagsAndTemplate}
                    />
                </div>

                <div className="tw-flex-1 tw-p-4">
                    <div className="tw-w-full tw-flex tw-items-start tw-justify-between">
                        <div className="tw-flex tw-items-center tw-gap-x-2">
                            <Dropdown
                                onSelect={(task) => updateDeliverableTask(task as DeliverableTask)}
                                className="custom-dropdown_alt"
                            >
                                <Dropdown.Toggle
                                    variant="secondary"
                                    id="dropdown-basic"
                                    className="dropdown__toggle"
                                    style={{ minWidth: '112px' }}
                                >
                                    {deliverable.task}
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="dropdown__toggle--menu">
                                    <Dropdown.Item eventKey={DeliverableTask.CREATE_NEW_AD}>
                                        {DeliverableTask.CREATE_NEW_AD}
                                    </Dropdown.Item>
                                    <Dropdown.Item eventKey={DeliverableTask.PRODUCT_PHOTOGRAPHY}>
                                        {DeliverableTask.PRODUCT_PHOTOGRAPHY}
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>

                            {deliverable.task === DeliverableTask.CREATE_NEW_AD && (
                                <>
                                    <Dropdown
                                        onSelect={(platform) =>
                                            updateDeliverablePlatform(platform as Platform)
                                        }
                                        className="custom-dropdown_alt"
                                    >
                                        <Dropdown.Toggle
                                            variant="secondary"
                                            id="dropdown-platform"
                                            className="dropdown__toggle"
                                            style={{ minWidth: '110px' }}
                                        >
                                            {deliverable.platform || 'Select Platform'}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu className="dropdown__toggle--menu">
                                            {DELIVERABLE_PLATFORM_ARRAY.map((platform) => (
                                                <Dropdown.Item eventKey={platform} key={platform}>
                                                    {platform}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>

                                    <Dropdown
                                        onSelect={(assetType) =>
                                            updateDeliverableAssetType(assetType as AssetType)
                                        }
                                        className="custom-dropdown_alt"
                                    >
                                        <Dropdown.Toggle
                                            variant="secondary"
                                            id="dropdown-asset-type"
                                            className="dropdown__toggle"
                                            style={{ minWidth: '110px' }}
                                        >
                                            {deliverable.asset_type || 'Select Asset Type'}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu className="dropdown__toggle--menu">
                                            {getAssetTypeForPlatform(deliverable.platform).map(
                                                (assetType) => (
                                                    <Dropdown.Item
                                                        eventKey={assetType}
                                                        key={assetType}
                                                    >
                                                        {assetType}
                                                    </Dropdown.Item>
                                                ),
                                            )}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </>
                            )}
                        </div>

                        <div className="tw-flex tw-items-center tw-gap-x-2 tw-items-stretch">
                            {/* <div className="tw-border tw-rounded-md tw-border-gray-250 tw-px-2 tw-py-1 tw-flex tw-items-center tw-gap-x-1 tw-cursor-pointer hover:tw-bg-gray-200">
                                <i className="ph ph-square-half-bottom lg"></i>
                                <div className="tw-hidden xl:tw-block">Change Template</div>
                            </div> */}
                            {deliverable.user_product_path &&
                                deliverable.task === DeliverableTask.CREATE_NEW_AD && (
                                    <div className="tw-flex tw-items-center tw-flex-col">
                                        <div className="tw-text-sm">Replace Background</div>
                                        <Form.Check
                                            type="checkbox"
                                            className="tw-scale-150 tw-flex tw-items-center tw-cursor-pointer"
                                            defaultChecked={
                                                deliverable.ad_product_photo ? true : false
                                            }
                                            onChange={(e) =>
                                                updateDeliverableAdProductPhoto(e.target.checked)
                                            }
                                        />
                                    </div>
                                )}
                            <AttachProductImage
                                deliverable={deliverable}
                                updateDeliverableUserProductPhoto={
                                    updateDeliverableUserProductPhoto
                                }
                            />
                            <InputGroup className="tw-w-fit custom-dropdown ">
                                <Dropdown autoClose="outside">
                                    <Dropdown.Toggle
                                        variant="outline-primary"
                                        className="tw-border tw-rounded-md tw-border-gray-250 tw-px-2 tw-py-1 dropdown__toggle iconbtn"
                                        id="dropdown-autoclose-inside"
                                    >
                                        <i className="ph-bold ph-plus"></i>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="dropdown__toggle--menu">
                                        <Dropdown.Item
                                            onClick={() =>
                                                addSubDeliverable(
                                                    deliverable.deliverable_number,
                                                    true,
                                                )
                                            }
                                        >
                                            Add Resize Option
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            onClick={() =>
                                                addSubDeliverable(
                                                    deliverable.deliverable_number,
                                                    false,
                                                )
                                            }
                                        >
                                            Add Variant Option
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </InputGroup>
                        </div>
                    </div>
                    <div className="tw-w-full tw-pr-4 xl:tw-pr-10 tw-mt-2">
                        {isEditing ? (
                            <Form.Control
                                as="textarea"
                                ref={textareaRef}
                                value={deliverable.content}
                                onChange={updateDeliverableContent}
                                onKeyDown={handleKeyDown}
                                placeholder="Describe the deliverable..."
                                rows={1}
                                autoFocus={true}
                                onBlur={() => setIsEditing(false)}
                            />
                        ) : (
                            <div
                                className="tw-text-base tw-cursor-pointer"
                                onClick={() => setIsEditing(true)}
                            >
                                {deliverable.content.length > 0
                                    ? deliverable.content
                                    : 'Tap to edit deliverable content'}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div
                className="tw-flex tw-items-center tw-jutify-center tw-border tw-rounded-md tw-p-2 tw-border-gray-250 tw-cursor-pointer"
                onClick={() => deleteDeliverables(deliverable.deliverable_number)}
            >
                <i className="ph-bold ph-trash lg"></i>
            </div>
        </div>
    );
};

export default Deliverable;
